import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type AtlasGqlTableViewsQuery,
  TableViewsDocument,
  useCreateTableViewMutation,
  useTableViewLazyQuery,
  useTableViewsQuery,
  useUpdateTableViewMutation,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { getItem, setItem } from 'utils/storage';

import type { DashboardLayoutState } from '../types';

import { validateAndMigrate } from './validateAndMigrate';

/** Namespaced + versioned so it can never collide with a real data-table view id. */
const RESERVED_TABLE_NAME = 'dashboard:widget-layout:v1';
const RESERVED_VIEW_NAME = '__dashboard__';
const STORAGE_PREFIX = 'DASHBOARD_WIDGET_LAYOUT';
const SAVE_DEBOUNCE_MS = 750;

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseDashboardPersistenceResult {
  /** Layout to render: server record if present, else localStorage, else seed. */
  loadedLayout: DashboardLayoutState;
  /** True once the initial server read has settled. */
  isLoaded: boolean;
  /** Persist a layout (immediate localStorage + debounced server write). */
  persist: (state: DashboardLayoutState) => void;
  saveStatus: SaveStatus;
}

/**
 * Per-user dashboard layout persistence, reusing the generated `TableView` GraphQL
 * trio + `customFields` JSON under a reserved tableName. localStorage is a
 * flash-free cache; the server record is the source of truth. No backend or
 * `.gql` changes — and no existing persistence code is modified.
 */
export function useDashboardPersistence(seed: DashboardLayoutState): UseDashboardPersistenceResult {
  const { customer } = useAccountContext();
  const storageKey = `${STORAGE_PREFIX}::${customer?.id ?? 'anon'}`;

  const { data: listData, loading: listLoading } = useTableViewsQuery({
    variables: { tableName: RESERVED_TABLE_NAME },
    fetchPolicy: 'cache-and-network',
  });
  const recordId: string | undefined = listData?.tableViews?.[0]?.id;

  const [fetchView, { data: viewData, loading: viewLoading }] = useTableViewLazyQuery();
  useEffect(() => {
    if (recordId) {
      fetchView({ variables: { id: recordId } });
    }
  }, [recordId, fetchView]);

  const [createTableView] = useCreateTableViewMutation();
  const [updateTableView] = useUpdateTableViewMutation();

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  // Lets the unmount-time flush still run its network write while skipping any
  // saveStatus update on an already-unmounted component.
  const mountedRef = useRef(true);

  // Synchronous localStorage seed for flash-free first paint.
  const localLayout = useMemo<DashboardLayoutState>(() => {
    const raw = getItem(storageKey, true);
    return validateAndMigrate(raw, seed);
  }, [storageKey, seed]);

  const serverCustomFields = viewData?.tableView?.customFields;
  const isLoaded = !listLoading && (recordId ? !viewLoading && viewData !== undefined : true);

  const loadedLayout = useMemo<DashboardLayoutState>(() => {
    if (recordId && serverCustomFields !== undefined) {
      return validateAndMigrate(serverCustomFields, seed);
    }
    return localLayout;
  }, [recordId, serverCustomFields, localLayout, seed]);

  // Refs that drive the single-in-flight save loop.
  const recordIdRef = useRef<string | undefined>(recordId);
  useEffect(() => {
    if (recordId) {
      recordIdRef.current = recordId;
    }
  }, [recordId]);

  // Tracks whether the TableViews list query has resolved at least once, so we
  // never CREATE a record before we know whether the server already has one.
  const listSettledRef = useRef(false);
  useEffect(() => {
    if (!listLoading) {
      listSettledRef.current = true;
    }
  }, [listLoading]);

  const latestRef = useRef<DashboardLayoutState | null>(null);
  const inFlightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const flush = useCallback(async () => {
    if (inFlightRef.current) {
      return; // a save is running; it re-checks latestRef when it finishes
    }
    const state = latestRef.current;
    if (!state) {
      return;
    }
    // If we'd have to CREATE (no known record id) but the list query that tells us
    // whether a record already exists hasn't resolved yet, defer — creating now
    // could duplicate an existing per-user record. Re-arm and retry shortly.
    if (!recordIdRef.current && !listSettledRef.current) {
      timerRef.current = setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
      return;
    }
    latestRef.current = null;
    inFlightRef.current = true;
    if (mountedRef.current) {
      setSaveStatus('saving');
    }

    try {
      const id = recordIdRef.current;
      if (id) {
        await updateTableView({ variables: { input: { id, customFields: state } } });
      } else {
        const result = await createTableView({
          variables: {
            input: {
              tableName: RESERVED_TABLE_NAME,
              name: RESERVED_VIEW_NAME,
              customFields: state,
            },
          },
          update: (cache, { data }) => {
            const created = data?.createTableView;
            if (!created) {
              return;
            }
            cache.updateQuery(
              { query: TableViewsDocument, variables: { tableName: RESERVED_TABLE_NAME } },
              (existing: AtlasGqlTableViewsQuery | undefined) =>
                existing
                  ? { ...existing, tableViews: [...(existing.tableViews ?? []), created] }
                  : existing
            );
          },
        });
        const createdId = result.data?.createTableView?.id;
        if (createdId) {
          recordIdRef.current = createdId;
        }
      }
      if (mountedRef.current) {
        setSaveStatus('saved');
      }
    } catch {
      if (mountedRef.current) {
        setSaveStatus('error');
      }
    } finally {
      inFlightRef.current = false;
      if (latestRef.current) {
        void flush(); // a newer state arrived mid-save
      }
    }
  }, [createTableView, updateTableView]);

  const persist = useCallback(
    (state: DashboardLayoutState) => {
      setItem(storageKey, state, true); // immediate, flash-free cache
      latestRef.current = state;
      setSaveStatus('saving');
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
    },
    [storageKey, flush]
  );

  // Flush a pending (debounced) save on tab hide and on unmount, so a change made
  // within the debounce window isn't lost when the user navigates away.
  useEffect(() => {
    mountedRef.current = true;
    const flushPending = () => {
      if (latestRef.current) {
        void flush();
      }
    };
    window.addEventListener('pagehide', flushPending);
    return () => {
      // Mark unmounted first so the flush below still performs its network write but
      // skips its saveStatus updates (no update-on-unmounted-component warning).
      mountedRef.current = false;
      window.removeEventListener('pagehide', flushPending);
      clearTimeout(timerRef.current);
      flushPending();
    };
  }, [flush]);

  return { loadedLayout, isLoaded, persist, saveStatus };
}
